<!--
  This dialog will be used to rate to order in DeliveryOrders.
-->
<template>
  <dialog-form
    :show-dialog="isShowDialog"
    :is-disabled="isDisabled"
    @open="dialogOpen"
    @cancel="hideDialog"
    @submit="submitRate"
  >
    <template #title>
      Rate Order
    </template>
    <div
      v-if="rateData && rateValue && rateValue.length"
      class="rate-delivery-order"
    >
      <fieldset
        v-for="(item, index) in rateData"
        :key="index"
      >
        <h6 class="pb-2">
          {{ item.title }}
        </h6>
        <div
          v-if="item.rating?.type === 'star'"
          class="rate-container"
        >
          <el-rate
            v-model="rateValue[index].starRating"
            :max="item.rating?.maxNumStars || 5"
            size="large"
            @change="onChangeStarRating(index)"
          />
        </div>
        <div
          v-else
          class="rate-container rate-button-container"
        >
          <div
            v-for="(tagItem, tagIndex) in item.rating?.tagsList"
            :key="tagIndex"
            :class="'rate-button-container ' + (rateValue[index].starRating === tagItem.starRating && 'selected')"
            @click="rateValue[index].starRating = tagItem.starRating"
          >
            <i
              v-if="getIconName(tagItem.iconName)"
              :class="getIconName(tagItem.iconName)"
            />
            <div
              v-else
              class="rate-button"
            >
              {{ tagItem.title }}
            </div>
          </div>
        </div>
        <div
          v-if="getStarToTagItem(index)"
          class="rating-tags"
        >
          <p class="mt-10">
            {{ getStarToTagItem(index).tagsTitle }}
          </p>
          <div class="tag-container">
            <div
              v-for="(tagItem, tIndex) in getStarToTagItem(index).tagList"
              :key="tIndex"
              :class="isSelectedTag(tagItem, index) ? 'tag selected' : 'tag'"
              @click="onToggleTagItem(tagItem, index)"
            >
              <p>{{ tagItem.tagName }}</p>
            </div>
          </div>
        </div>
        <sanitized-textarea
          id="notes"
          v-model="rateValue[index].reviewText"
          class="mt-20 mb-20"
          :placeholder="item.rating?.title ?? ''"
          minlength="10"
          autosize
        />
      </fieldset>
    </div>
  </dialog-form>
</template>

<script lang="ts">
import { toRefs, Ref, ref, computed, PropType } from 'vue';

import { OrderRateData } from '@/models/gourmet';

export default {
  props: {
    isShowDialog: {
      type: Boolean,
      default: false,
    },
    rateData: {
      type: Array as PropType<OrderRateData[]>,
      default: () => [],
    },
  },
  emits: ['submit', 'cancel'],
  setup: (props: any, context: any) => {
    const { emit } = context;
    const { rateData } = toRefs(props);
    const rateValue: Ref<any[]> = ref([]);

    const isDisabled = computed(() => {
      const emptyRates = rateValue.value.filter((item) => !item.starRating);

      if (emptyRates.length) {
        return true;
      }

      return false;
    });

    const dialogOpen = () => {
      if (!rateData.value) {
        rateValue.value = [];
        return;
      }

      // setup rateValue
      const value: any[] = [];
      rateData.value.map((item: any) => {
        value.push({
          target: item.target,
          starRating: 0,
          reviewText: '',
          tagList: [],
        });
      });

      rateValue.value = value;
    };

    const getStarToTagItem = (index: number) => {
      const starRating = rateValue.value[index]?.starRating;

      if (!starRating) {
        return null;
      }

      const chosenStarToTag = rateData.value[index]?.rating?.tagsList?.find((item: any) => item.starRating === starRating);

      if (!chosenStarToTag?.tagList?.length) {
        return null;
      }

      return chosenStarToTag;
    };

    const getIconName = (name?: string | null) => {
      if (!name) {
        return
      }

      if (name.includes('thumbs-down')) {
        return 'fa fa-thumbs-down'
      }

      if (name.includes('thumbs-up')) {
        return 'fa fa-thumbs-up'
      }

      if (name.includes('favorite')) {
        return 'fa fa-heart'
      }
    }

    const isSelectedTag = (tagItem: any, index: number) => {
      const selectedTag = rateValue.value[index]?.tagList?.find((item: any) => item.tagId === tagItem.tagId);

      if (selectedTag) {
        return true;
      }

      return false;
    };

    const onToggleTagItem = (tagItem: any, index: number) => {
      const tagIndex = rateValue.value[index]?.tagList?.findIndex((item: any) => item.tagId === tagItem.tagId);

      if (tagIndex > -1) {
        rateValue.value[index].tagList.splice(tagIndex, 1); // remove rating feedback tag
      } else {
        rateValue.value[index].tagList.push(tagItem); // add rating feedback tag
      }
    };

    const onChangeStarRating = (index: number) => {
      rateValue.value[index].tagList = [];
    };

    const submitRate = () => {
      emit('submit', rateValue.value);
    };

    const hideDialog = () => {
      emit('cancel');
    };

    return {
      dialogOpen,
      submitRate,
      hideDialog,
      getStarToTagItem,
      isSelectedTag,
      onToggleTagItem,
      onChangeStarRating,
      getIconName,

      isDisabled,
      rateValue,
    };
  },
};
</script>

<style lang="scss">
.rate-delivery-order {
  .rate-container {
    position: relative;

    .el-rate {
      height: 30px;

      .el-rate__icon {
        font-size: 30px;
      }
    }
  }

  .rate-button-container {
    display: flex;
    gap: 10px;

    .rate-button-container {
      cursor: pointer;

      i {
        font-size: 20px;
        border-radius: 50%;
        border: 1px solid;
        padding: 5px;
      }

      .rate-button {
        padding: 7px 10px;
        border-radius: 20px;
        border: 2px solid gray;
      }

      &:hover {
        i, .rate-button {
          border-color: red;
        }
      }

      &.selected {
        i, .rate-button {
          border-color: red;
          color: red;
        }
      }
    }
  }

  .rating-tags {
    .tag-container {
      display: flex;
      flex-wrap: wrap;

      .tag {
        padding: 7px 10px;
        border-radius: 20px;
        border: 2px solid gray;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 5px;
        cursor: pointer;

        &.selected {
          border-color: #6bd098;
        }
      }
    }
  }
}
</style>
